import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  maxHeight?: string;
}

export function Sheet({ open, onClose, title, children, maxHeight = "80vh" }: SheetProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end pointer-events-none">
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0 bg-black/40 pointer-events-auto"
              onClick={onClose}
            />
            <motion.div
              ref={ref}
              role="dialog"
              aria-modal="true"
              aria-label={title ?? "Dialog"}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              style={{ maxHeight }}
              className="relative w-full bg-bg border-t border-hairline rounded-t-2xl overflow-hidden flex flex-col pointer-events-auto"
            >
              <div className="flex justify-center pt-2.5 pb-1 shrink-0">
                <div className="w-10 h-0.5 bg-fg/20" />
              </div>
              {title && (
                <div className="px-4 pb-3 border-b border-hairline shrink-0">
                  <h3 className="text-base font-semibold text-fg">{title}</h3>
                </div>
              )}
              <div className="overflow-y-auto pb-safe">{children}</div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>,
    document.body,
  );
}
